import moment from "moment-timezone";

export const CST_TIMEZONE = "America/Chicago";
export const BUSINESS_HOURS_LABEL = "12:00 PM – 11:00 AM";

const BUSINESS_DAY_START_HOUR = 12;
const BUSINESS_DAY_START_MINUTE = 0;
const BUSINESS_DAY_END_HOUR = 11;
const BUSINESS_DAY_END_MINUTE = 0;

export const getCstNow = () => moment().tz(CST_TIMEZONE);

export const getCstToday = () => getCstNow().clone().startOf("day").toDate();

export const getCstTodayEnd = () => getCstNow().clone().endOf("day").toDate();

export const getCstMonthStart = () => getCstNow().clone().startOf("month").toDate();

export const getCstMonthEnd = () => getCstNow().clone().endOf("month").toDate();

const CST_PARSE_FORMATS = [
  "MM/DD/YYYY hh:mm:ss A",
  "MM/DD/YYYY hh:mm A",
  "MM/DD/YYYY h:mm:ss A",
  "MM/DD/YYYY h:mm A",
  "YYYY-MM-DDTHH:mm:ss.SSS",
  "YYYY-MM-DDTHH:mm:ss",
  "YYYY-MM-DDTHH:mm",
  "YYYY-MM-DD HH:mm:ss",
  "YYYY-MM-DD HH:mm",
  "YYYY-MM-DD",
  "MM/DD/YYYY",
  moment.ISO_8601,
];

export const toCstMoment = (date) => {
  if (date == null || date === "") return getCstNow();
  if (moment.isMoment(date)) return date.clone().tz(CST_TIMEZONE);
  if (date instanceof Date) return moment(date).tz(CST_TIMEZONE);
  if (typeof date === "string") {
    const trimmed = date.trim();
    if (trimmed.endsWith("Z") || /(?:T|\s)\d{2}:\d{2}(?::\d{2})?([+-]\d{2}:?\d{2})$/.test(trimmed)) {
      return moment(trimmed).tz(CST_TIMEZONE);
    }
    const parsed = moment.tz(trimmed, CST_PARSE_FORMATS, true, CST_TIMEZONE);
    if (parsed.isValid()) return parsed;
    return moment.tz(trimmed, CST_TIMEZONE);
  }
  return moment(date).tz(CST_TIMEZONE);
};

const calendarMoment = (dateLike) => {
  if (dateLike == null || dateLike === "") return getCstNow();
  if (moment.isMoment(dateLike)) return dateLike.clone().tz(CST_TIMEZONE);
  if (typeof dateLike === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateLike.trim())) {
    return moment.tz(dateLike.trim(), "YYYY-MM-DD", CST_TIMEZONE);
  }
  return toCstMoment(dateLike);
};

export const toDateOnlyKey = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    return value.trim();
  }
  const parsed = calendarMoment(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
};

export const businessDayStart = (dateLike) =>
  calendarMoment(toDateOnlyKey(dateLike) || dateLike)
    .startOf("day")
    .hour(BUSINESS_DAY_START_HOUR)
    .minute(BUSINESS_DAY_START_MINUTE)
    .second(0)
    .millisecond(0);

export const businessDayEnd = (dateLike) =>
  calendarMoment(toDateOnlyKey(dateLike) || dateLike)
    .startOf("day")
    .add(1, "day")
    .hour(BUSINESS_DAY_END_HOUR)
    .minute(BUSINESS_DAY_END_MINUTE)
    .second(0)
    .millisecond(0)
    .subtract(1, "millisecond");

export const currentBusinessDateKey = (nowLike) => {
  const current = calendarMoment(nowLike);
  const startToday = current
    .clone()
    .startOf("day")
    .hour(BUSINESS_DAY_START_HOUR)
    .minute(BUSINESS_DAY_START_MINUTE)
    .second(0)
    .millisecond(0);

  if (current.isSameOrAfter(startToday)) {
    return current.format("YYYY-MM-DD");
  }

  const endToday = current
    .clone()
    .startOf("day")
    .hour(BUSINESS_DAY_END_HOUR)
    .minute(BUSINESS_DAY_END_MINUTE)
    .second(0)
    .millisecond(0);

  if (current.isBefore(endToday)) {
    return current.clone().subtract(1, "day").format("YYYY-MM-DD");
  }

  return current.format("YYYY-MM-DD");
};

export const getCurrentBusinessDate = () =>
  moment.tz(currentBusinessDateKey(), "YYYY-MM-DD", CST_TIMEZONE).startOf("day").toDate();

export const getBusinessDateRange = (startDateLike, endDateLike) => ({
  start: businessDayStart(startDateLike).toDate(),
  end: businessDayEnd(endDateLike || startDateLike).toDate(),
});

export const isDateTimeInBusinessRange = (value, rangeStart, rangeEnd) => {
  const date = toCstMoment(value);
  if (!date.isValid()) return false;
  const { start, end } = getBusinessDateRange(rangeStart, rangeEnd);
  return date.isBetween(moment(start), moment(end), null, "[]");
};

export const toCstStartOfDay = (date) => toCstMoment(date).startOf("day").toDate();

export const toCstEndOfDay = (date) => toCstMoment(date).endOf("day").toDate();

export const formatCstDate = (date, formatStr = "YYYY-MM-DD") =>
  toCstMoment(date).format(formatStr);

export const formatCstDateTime = (date, formatStr = "MMM D, YYYY h:mm A") =>
  toCstMoment(date).format(formatStr);

export const formatCstDateTimeForApi = (date) =>
  toCstMoment(date).format("MM/DD/YYYY hh:mm A");

export const formatCstDateTimeLocal = (date) =>
  toCstMoment(date).format("YYYY-MM-DDTHH:mm");

export const parseCstDateTimeLocal = (value) =>
  moment.tz(value, "YYYY-MM-DDTHH:mm", CST_TIMEZONE).toDate();

/** Analytics API timestamps are always in CST, e.g. "2026-07-26 09:30 AM" */
export const parseAnalyticsTimestamp = (timestamp) =>
  moment.tz(timestamp, "YYYY-MM-DD hh:mm A", CST_TIMEZONE);

export const formatCstDisplayDateTime = (date) =>
  toCstMoment(date).format("MM/DD/YYYY hh:mm A");

export const formatCstDateForApi = (date, { endOfDay = false } = {}) =>
  endOfDay
    ? toCstMoment(date).endOf("day").format("MM/DD/YYYY hh:mm A")
    : toCstMoment(date).startOf("day").format("MM/DD/YYYY hh:mm A");

export const formatCstDateRangeLabel = (start, end) =>
  `${formatCstDate(start, "MM/DD/YYYY")} - ${formatCstDate(end, "MM/DD/YYYY")}`;

export const getDefaultDateRangeToday = () => {
  const day = getCurrentBusinessDate();
  return {
    start: toCstStartOfDay(day),
    end: toCstEndOfDay(day),
    label: "Today",
  };
};

export const getDefaultDateRangeThisMonth = () => {
  const business = moment.tz(currentBusinessDateKey(), "YYYY-MM-DD", CST_TIMEZONE);
  return {
    start: business.clone().startOf("month").toDate(),
    end: business.clone().endOf("day").toDate(),
    label: "This Month",
  };
};

export const getDefaultDateRangeLast7Days = () => {
  const business = moment.tz(currentBusinessDateKey(), "YYYY-MM-DD", CST_TIMEZONE);
  return {
    start: business.clone().subtract(6, "days").startOf("day").toDate(),
    end: business.clone().endOf("day").toDate(),
    label: "Last 7 Days",
  };
};

export const getCstHour = () => getCstNow().hour();

export const isSameCstDay = (left, right) =>
  formatCstDate(left) === formatCstDate(right);

export const isSameCstMonth = (left, right) =>
  formatCstDate(left, "YYYY-MM") === formatCstDate(right, "YYYY-MM");
